import { KPICard, ChartData, InsightData } from '@/data/mockData';

// Utility functions for data export

export const exportToCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) {
    throw new Error('Nenhum dado disponível para exportação');
  }

  // Convert data to CSV format
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values that might contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToPDF = async (
  kpis: KPICard[], 
  revenueData: ChartData[], 
  insights: InsightData[], 
  filename: string
) => {
  // Dynamic import to reduce bundle size
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();

  // PDF Configuration
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  let yPosition = margin;

  // Helper function to add text with word wrap
  const addText = (text: string, x: number, y: number, maxWidth: number, fontSize = 12) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * fontSize * 0.4);
  };

  // Helper function to check if we need a new page
  const checkNewPage = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Dashboard Analytics - Relatório de Performance', margin, yPosition);
  yPosition += 15;

  // Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, margin, yPosition);
  yPosition += 20;

  // KPIs Section
  checkNewPage(60);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Indicadores Chave de Performance (KPIs)', margin, yPosition);
  yPosition += 10;

  // KPI Table
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const kpiTableData = kpis.map(kpi => [
    kpi.title,
    kpi.value,
    `${kpi.change > 0 ? '+' : ''}${kpi.change}%`,
    kpi.description
  ]);

  // Simple table implementation
  const colWidths = [40, 30, 25, 80];
  const headers = ['Métrica', 'Valor', 'Variação', 'Descrição'];
  
  // Table headers
  doc.setFont('helvetica', 'bold');
  let xPos = margin;
  headers.forEach((header, index) => {
    doc.text(header, xPos, yPosition);
    xPos += colWidths[index];
  });
  yPosition += 8;

  // Table rows
  doc.setFont('helvetica', 'normal');
  kpiTableData.forEach(row => {
    checkNewPage(15);
    xPos = margin;
    row.forEach((cell, index) => {
      const cellText = doc.splitTextToSize(cell, colWidths[index] - 2);
      doc.text(cellText, xPos, yPosition);
      xPos += colWidths[index];
    });
    yPosition += 12;
  });

  yPosition += 10;

  // Revenue Data Section
  checkNewPage(40);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Dados de Receita (Últimos 12 Meses)', margin, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  revenueData.forEach(item => {
    checkNewPage(8);
    const text = `${item.name}: R$ ${item.revenue?.toLocaleString('pt-BR')} | Usuários: ${item.users?.toLocaleString('pt-BR')} | Conversão: ${item.conversion?.toFixed(1)}%`;
    doc.text(text, margin, yPosition);
    yPosition += 6;
  });

  yPosition += 10;

  // Insights Section
  checkNewPage(40);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Insights Acionáveis', margin, yPosition);
  yPosition += 10;

  insights.forEach((insight, index) => {
    checkNewPage(25);
    
    // Insight title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    yPosition = addText(`${index + 1}. ${insight.title}`, margin, yPosition, pageWidth - 2 * margin, 12);
    yPosition += 2;
    
    // Insight description
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    yPosition = addText(insight.description, margin + 5, yPosition, pageWidth - 2 * margin - 5, 10);
    yPosition += 2;
    
    // Insight action
    if (insight.action) {
      doc.setFont('helvetica', 'italic');
      yPosition = addText(`Ação recomendada: ${insight.action}`, margin + 5, yPosition, pageWidth - 2 * margin - 5, 10);
    }
    
    // Priority badge
    doc.setFont('helvetica', 'bold');
    const priorityText = `Prioridade: ${insight.priority.toUpperCase()}`;
    doc.text(priorityText, margin + 5, yPosition);
    yPosition += 15;
  });

  // Footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Página ${i} de ${totalPages} - Dashboard Analytics`,
      pageWidth - margin - 50,
      pageHeight - 10
    );
  }

  // Save the PDF
  doc.save(`${filename}.pdf`);
};

// Prepare data for CSV export
export const prepareKPIsForCSV = (kpis: KPICard[]) => {
  return kpis.map(kpi => ({
    'Métrica': kpi.title,
    'Valor': kpi.value,
    'Variação (%)': kpi.change,
    'Tipo de Variação': kpi.changeType === 'increase' ? 'Aumento' : 'Diminuição',
    'Descrição': kpi.description
  }));
};

export const prepareRevenueDataForCSV = (data: ChartData[]) => {
  return data.map(item => ({
    'Período': item.name,
    'Receita (R$)': item.revenue || 0,
    'Usuários': item.users || 0,
    'Taxa de Conversão (%)': item.conversion || 0,
    'Data': item.date || ''
  }));
};

export const prepareInsightsForCSV = (insights: InsightData[]) => {
  return insights.map(insight => ({
    'Título': insight.title,
    'Tipo': insight.type,
    'Descrição': insight.description,
    'Ação Recomendada': insight.action || 'Nenhuma',
    'Prioridade': insight.priority
  }));
};

export const prepareMarketingDataForCSV = (data: ChartData[]) => {
  return data.map(item => ({
    'Canal': item.name,
    'Investimento (R$)': item.value,
    'Taxa de Conversão (%)': item.conversion || 0
  }));
};